import { useState } from 'react';
import type { PublicView, PrivateView } from '@ringmaster/shared';
import { useRoom } from '../net/useRoom.js';
import { PlayerList } from '../ui/PlayerList.js';
import { FixedActionBar } from '../ui/FixedActionBar.js';
import { Glyph } from '../ui/Glyph.js';
import { ParticleLayer } from '../fx/particles/ParticleLayer.js';

/**
 * DiscussScreen — day discussion phase.
 * The countdown lives in the shared GameHeader (server-driven). Living roster
 * (read-only) + "Talk it out at the table" hint + a "skip discussion" vote:
 * when EVERY living player approves, the game jumps straight to the vote.
 * Seer sees their journal collapsed at bottom.
 */
export function DiscussScreen({
  publicView,
  privateView,
  actionOffset = 0,
}: {
  publicView: PublicView;
  privateView: PrivateView;
  actionOffset?: number;
}) {
  const { send } = useRoom();
  const [journalOpen, setJournalOpen] = useState(false);
  const livePlayers = publicView.players.filter((p) => p.alive);

  // Skip-discussion state: READY during DISCUSS = "I'm done talking".
  const serverApproved = privateView.action.type === 'READY' && privateView.action.done;
  const [localApproved, setLocalApproved] = useState(false);
  const approvedSkip = serverApproved || localApproved;
  const skipVotes = publicView.skipVotes;

  function handleSkip() {
    setLocalApproved(true);
    send({ type: 'READY' });
  }

  return (
    <div
      className="game-screen"
      style={{
        position: 'relative',
        minHeight: '100dvh',
        display: 'flex',
        flexDirection: 'column',
        // Warm tavern tint — candlelit amber over the ink.
        background: 'linear-gradient(180deg, #0a0603 0%, #2b1a0e 100%)',
        color: 'var(--text-primary)',
        padding: 'var(--space-5)',
        paddingTop: 'max(var(--space-7), env(safe-area-inset-top, 0px) + var(--space-5))',
        // Room for the fixed skip bar (+ host RingmasterBar).
        paddingBottom: 110 + actionOffset,
        gap: 'var(--space-5)',
        overflow: 'hidden',
      }}
    >
      {/* Fireflies drifting in the tavern warmth (phone-tier capped; sparse). */}
      <ParticleLayer preset="fireflies" intensity={0.7} slot={0} />

      {/* Header */}
      <div style={{ position: 'relative', zIndex: 1, textAlign: 'center' }}>
        <h1
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 26,
            margin: '0 0 var(--space-2)',
            letterSpacing: '0.02em',
          }}
        >
          Day — Discuss
        </h1>
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0 }}>
          Round {publicView.round}
        </p>
      </div>

      {/* Hint */}
      <div
        style={{
          position: 'relative',
          zIndex: 1,
          background: 'rgba(126,92,68,0.1)',
          border: '1px solid rgba(126,92,68,0.25)',
          borderRadius: 16,
          padding: 'var(--space-4)',
          textAlign: 'center',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 'var(--space-2)',
        }}
      >
        <Glyph name="scales" size={18} tone="gold" />
        <p style={{ color: 'var(--text-secondary)', fontSize: 14, margin: 0, lineHeight: 1.5 }}>
          Talk it out at the table. Who do you suspect?
        </p>
      </div>

      {/* Living players */}
      <div style={{ position: 'relative', zIndex: 1, flex: 1 }}>
        <div style={{ color: 'var(--text-tertiary)', fontSize: 12, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: 'var(--space-3)' }}>
          Still alive ({livePlayers.length})
        </div>
        <PlayerList players={publicView.players} selectable={false} />
      </div>

      {/* Seer journal (collapsible) */}
      {privateView.seerHistory && privateView.seerHistory.length > 0 && (
        <div style={{ position: 'relative', zIndex: 1 }}>
          <button
            type="button"
            className="rm-hover-row"
            onClick={() => setJournalOpen((o) => !o)}
            style={{
              width: '100%',
              background: 'rgba(255,255,255,0.05)',
              border: '1px solid var(--surface-stroke)',
              borderRadius: 14,
              padding: '12px var(--space-4)',
              color: 'var(--text-secondary)',
              fontFamily: 'var(--font-ui)',
              fontSize: 14,
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              minHeight: 44,
            }}
          >
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
              <Glyph name="seerEye" size={16} tone="arcana" />
              Investigation Journal ({privateView.seerHistory.length})
            </span>
            <span>{journalOpen ? '▲' : '▼'}</span>
          </button>
          {journalOpen && (
            <div
              style={{
                marginTop: 4,
                background: 'rgba(0,0,0,0.5)',
                border: '1px solid var(--surface-stroke)',
                borderRadius: 14,
                padding: 'var(--space-4)',
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
              }}
            >
              {privateView.seerHistory.map((entry) => (
                <div
                  key={`${entry.round}-${entry.targetId}`}
                  style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13, color: 'var(--text-secondary)' }}
                >
                  <span>N{entry.round}: {entry.targetName}</span>
                  <span style={{ color: entry.isWerewolf ? 'var(--blood-6, #ff5a4f)' : 'var(--verdigris-3, #3d7a6c)', fontWeight: 600 }}>
                    {entry.isWerewolf ? 'WOLF' : 'clear'}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Skip-discussion vote — unanimous among the living jumps to the vote. */}
      {privateView.alive && (
        <FixedActionBar offsetBottom={actionOffset}>
          <button
            type="button"
            className="rm-hover"
            disabled={approvedSkip}
            onClick={handleSkip}
            style={{
              width: '100%',
              minHeight: 52,
              fontFamily: 'var(--font-ui)',
              fontWeight: 600,
              fontSize: 15,
              borderRadius: 'var(--radius-pill)',
              padding: '14px 24px',
              border: approvedSkip
                ? '1px solid rgba(126,92,68,0.35)'
                : '1px solid rgba(126,92,68,0.5)',
              background: approvedSkip ? 'rgba(126,92,68,0.12)' : 'rgba(126,92,68,0.28)',
              color: approvedSkip ? 'var(--text-tertiary)' : '#f2d9a0',
              cursor: approvedSkip ? 'default' : 'pointer',
              transition: 'all var(--duration-base) var(--ease-standard)',
            }}
          >
            {approvedSkip
              ? `Waiting for the others… (${skipVotes ? `${skipVotes.approved}/${skipVotes.total}` : '…'})`
              : `Skip to the vote${skipVotes && skipVotes.approved > 0 ? ` (${skipVotes.approved}/${skipVotes.total})` : ''}`}
          </button>
        </FixedActionBar>
      )}
    </div>
  );
}
